/**
 * NotificationsPage — H-1. 알림보기
 *
 * - 필터 탭: 전체 + 공통코드 ZEU_NOTI_CATEGORY (서버 category 파라미터 필터)
 * - 섹션 라벨: 오늘 / 어제 / 이전
 * - 개별 읽음: provider.markRead(id) → 배지 감소 + hermes 직접 PATCH
 * - 모두 읽음: PATCH /indipia/notifications/read-all + provider.resetUnreadCount()
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, ErrorState, Page, PageHeader, Skeleton, Tabs } from '@indipia/ui-core';
import { useAppStore, useAuthStore } from '@indipia/shared';
import { fetchNotifications, markAllRead, markOneRead } from '../api/notifications';
import type { NotifItem } from '../api/notifications';
import { useNotifications } from '../../app/shell/notifications/NotificationProvider';
import { useAllMenuTree, resolveMenuCodeAction, type MenuNode } from '../../registry/menu-tree';
import { showInfoToast } from '../../app/shell/notifications/InfoToast';

// ── 알림 라우팅 ──────────────────────────────────────────────────────────────
//
// 메뉴 코드 기반 라우팅 — 알림 원장(webMenuCode)이 이동할 화면을 이미 가리킨다.
// TM_MENU 트리에서 코드로 경로를 찾고, targetId가 있으면 상세 세그먼트로 부착한다.
// webMenuCode가 없는데 appMenuCode만 있으면 앱 전용 화면 — 이동 없이 안내만 띄운다.
// 둘 다 없으면(또는 webMenuCode가 트리에 없으면) 화면 이동 없음.

const APP_ONLY_MESSAGE = '앱 전용 화면입니다. 인디피아 앱을 이용해주세요.';

function resolveAction(item: NotifItem, tree: MenuNode[] | undefined) {
  return resolveMenuCodeAction(tree, item.webMenuCode, item.appMenuCode, item.targetId, item.siteId);
}

// ── 표시 카테고리 ─────────────────────────────────────────────────────────────
//
// 분류 기준은 서버가 내려주는 notiCategory(ZEU_NOTI_CATEGORY, 표준 F-03 §6.1·§7.6)다.
// 탭 목록·이름은 공통코드(GET /indipia/init → useAppStore.codes)에서 읽고,
// 탭 전환 시 category 쿼리 파라미터로 서버 필터링한다. 과거 알림은 서버가 STUDIO로 내려준다.

const CATEGORY_GROUP = 'ZEU_NOTI_CATEGORY';
const ALL_TAB = 'ALL';
const DEFAULT_CATEGORY = 'STUDIO';

function useCategoryCodes() {
  const codes = useAppStore((s) => s.codes);
  return useMemo(
    () =>
      codes
        .filter((c) => c.codeGroupCode === CATEGORY_GROUP)
        .sort((a, b) => a.codeOrder - b.codeOrder),
    [codes],
  );
}

// ── 카테고리 비주얼 ───────────────────────────────────────────────────────────

interface CategoryVisual {
  badgeStyle: React.CSSProperties;
  iconBg: string;
  iconStroke: string;
  icon: React.ReactNode;
}

const CATEGORY_VISUAL: Record<string, CategoryVisual> = {
  CUSTOMER_CONSULT: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--shell-point) 10%, transparent)',
      color: 'var(--shell-point)',
    },
    iconBg: 'color-mix(in srgb, var(--shell-point) 10%, transparent)',
    iconStroke: 'var(--shell-point)',
    icon: (
      // 나의 전시장 LNB 아이콘(IcStore)과 동일 — 화면 이동처가 같으니 아이콘도 일관되게 맞춤
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l1.5-5h15L21 9"/>
        <path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"/>
        <path d="M9 21v-6h6v6"/>
      </svg>
    ),
  },
  STUDIO: {
    badgeStyle: {
      background: 'var(--gray-100)',
      color: 'var(--gray-600)',
    },
    iconBg: 'var(--gray-100)',
    iconStroke: 'var(--gray-600)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  PHOTO_ALBUM: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
      color: 'var(--color-info)',
    },
    iconBg: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
    iconStroke: 'var(--color-info)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    ),
  },
  SCHEDULE: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)',
      color: 'var(--gt-primary)',
    },
    iconBg: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)',
    iconStroke: 'var(--gt-primary)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  WORKLOG: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
      color: 'var(--color-success)',
    },
    iconBg: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
    iconStroke: 'var(--color-success)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
        <line x1="3" y1="20" x2="21" y2="20"/>
      </svg>
    ),
  },
  SUPPORT_CENTER: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
      color: 'var(--color-warning)',
    },
    iconBg: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
    iconStroke: 'var(--color-warning)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
      </svg>
    ),
  },
  ORDER_DELIVERY: {
    badgeStyle: {
      background: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)',
      color: 'var(--gt-primary)',
    },
    iconBg: 'color-mix(in srgb, var(--gt-primary) 12%, transparent)',
    iconStroke: 'var(--gt-primary)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 8l-9-5-9 5v8l9 5 9-5z"/>
        <polyline points="3.3 7 12 12 20.7 7"/>
        <line x1="12" y1="22" x2="12" y2="12"/>
      </svg>
    ),
  },
};

function getVisual(category: string | null | undefined): CategoryVisual {
  return CATEGORY_VISUAL[category ?? DEFAULT_CATEGORY] ?? CATEGORY_VISUAL[DEFAULT_CATEGORY];
}

// ── 날짜 헬퍼 ────────────────────────────────────────────────────────────────

type SectionKey = '오늘' | '어제' | '이전';

function getSectionKey(createdAt: string): SectionKey {
  const now = new Date();
  const dt = new Date(createdAt);
  if (dt.toDateString() === now.toDateString()) return '오늘';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dt.toDateString() === yesterday.toDateString()) return '어제';
  return '이전';
}

function formatTime(createdAt: string): string {
  const now = new Date();
  const dt = new Date(createdAt);
  const diffMin = Math.floor((now.getTime() - dt.getTime()) / 60000);

  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 6) return `${diffHr}시간 전`;

  const timeStr = dt.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dt.toDateString() === now.toDateString()) return `오늘 ${timeStr}`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dt.toDateString() === yesterday.toDateString()) return `어제 ${timeStr}`;

  return dt.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}

// ── 알림 카드 ────────────────────────────────────────────────────────────────

function NotiCard({
  item,
  categoryName,
  onRead,
}: {
  item: NotifItem;
  categoryName: string | undefined;
  onRead: (item: NotifItem) => void;
}) {
  const navigate = useNavigate();
  const { data: menuTree } = useAllMenuTree();
  const isUnread = !item.read;
  const visual = getVisual(item.notiCategory);
  const action = resolveAction(item, menuTree);

  function handleClick() {
    if (isUnread) onRead(item);
    if (action.kind === 'route') navigate(action.path);
    else if (action.kind === 'other-platform') showInfoToast(APP_ONLY_MESSAGE);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
      className="flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-all"
      style={
        isUnread
          ? {
              borderColor: 'color-mix(in srgb, var(--gt-primary) 30%, transparent)',
              background: 'color-mix(in srgb, var(--gt-primary) 5%, var(--shell-surface))',
            }
          : { borderColor: 'var(--shell-border)', background: 'var(--shell-surface)' }
      }
    >
      {/* 카테고리 아이콘 */}
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ background: visual.iconBg, color: visual.iconStroke }}
      >
        {visual.icon}
      </div>

      {/* 본문 */}
      <div className="min-w-0 flex-1">
        {/* 카테고리 배지 + 미읽음 점 */}
        <div className="mb-1.5 flex items-center gap-2">
          {categoryName && (
            <Badge style={visual.badgeStyle}>{categoryName}</Badge>
          )}
          {isUnread && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-error" />
          )}
        </div>

        {/* 메시지 */}
        <p className="mb-1.5 text-sm leading-relaxed text-text">
          {item.body}
        </p>

        {/* 시간 + 액션 */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">{formatTime(item.createdAt)}</span>
          {action.kind === 'route' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleClick(); }}
            >
              바로가기
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const memberId = useAuthStore((s) => s.user?.memberId);
  const { unreadCount, resetUnreadCount, decrementUnreadCount } = useNotifications();

  const categoryCodes = useCategoryCodes();
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const activeCategory = activeTab === ALL_TAB ? undefined : activeTab;
  const categoryName = (code: string | null | undefined) =>
    categoryCodes.find((c) => c.codeValue === (code ?? DEFAULT_CATEGORY))?.codeName;

  const { data: notifs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications', memberId, activeTab],
    queryFn: () => fetchNotifications(memberId!, activeCategory),
    enabled: !!memberId,
    staleTime: 0,
  });

  const markAllMutation = useMutation({
    mutationFn: () => markAllRead(memberId!, activeCategory),
    onSuccess: () => {
      if (activeCategory) {
        // 탭(카테고리) 단위 읽음 처리 — 뱃지 카운트는 현재 탭 목록의 미읽음 수만큼 차감
        const readCount = notifs.filter((n) => !n.read).length;
        for (let i = 0; i < readCount; i++) decrementUnreadCount();
      } else {
        resetUnreadCount();
      }
      // 모든 탭 캐시에 반영(전체 탭은 전체, 카테고리 탭은 해당 카테고리만)
      queryClient.setQueriesData<NotifItem[]>({ queryKey: ['notifications', memberId] }, (prev) =>
        prev?.map((n) =>
          (!activeCategory || (n.notiCategory ?? DEFAULT_CATEGORY) === activeCategory) ? { ...n, read: true } : n,
        ),
      );
    },
  });

  const markOneMutation = useMutation({
    mutationFn: (id: number) => markOneRead(id),
    onSuccess: (_, id) => {
      queryClient.setQueriesData<NotifItem[]>({ queryKey: ['notifications', memberId] }, (prev) =>
        prev?.map((n) => n.id === id ? { ...n, read: true } : n),
      );
      decrementUnreadCount();
    },
  });

  function handleMarkRead(item: NotifItem) {
    if (item.read || markOneMutation.variables === item.id) return;
    markOneMutation.mutate(item.id);
  }

  // 필터링은 서버(category 쿼리 파라미터)가 수행
  const filtered = notifs; // 서버가 category 파라미터로 이미 필터링

  // 섹션 분류
  const sections: { key: SectionKey; items: NotifItem[] }[] = [];
  const sectionMap = new Map<SectionKey, NotifItem[]>();
  const SECTION_ORDER: SectionKey[] = ['오늘', '어제', '이전'];
  for (const n of filtered) {
    const key = getSectionKey(n.createdAt);
    if (!sectionMap.has(key)) sectionMap.set(key, []);
    sectionMap.get(key)!.push(n);
  }
  for (const key of SECTION_ORDER) {
    if (sectionMap.has(key)) {
      sections.push({ key, items: sectionMap.get(key)! });
    }
  }

  return (
    <Page width="md">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            알림
            {unreadCount > 0 && (
              <Badge status="danger">{unreadCount > 99 ? '99+' : unreadCount}</Badge>
            )}
          </span>
        }
        extra={
          <Button
            type="button"
            variant="secondary"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending || isError || filtered.every((n) => n.read)}
            loading={markAllMutation.isPending}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            모두 읽음으로 표시
          </Button>
        }
      />

      <div className="space-y-5">
        {/* 필터 탭 */}
        <Tabs
          variant="line"
          tabs={[
            { label: '전체', value: ALL_TAB },
            ...categoryCodes.map((c) => ({ label: c.codeName, value: c.codeValue })),
          ]}
          value={activeTab}
          onChange={(v) => setActiveTab(String(v))}
        />

        {/* 로딩 */}
        {isLoading && (
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        )}

        {/* 조회 실패 — 빈 상태와 구분해 오류와 다시 시도를 보인다(DF-22) */}
        {!isLoading && isError && (
          <ErrorState
            description="알림을 불러오지 못했어요. 잠시 후 다시 시도해주세요."
            onRetry={() => void refetch()}
          />
        )}

        {/* 빈 상태 */}
        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState
            icon="🔔"
            title={!activeCategory ? '알림이 없습니다' : `${categoryName(activeCategory) ?? ''} 알림이 없습니다`.trim()}
          />
        )}

        {/* 섹션별 목록 */}
        {sections.map(({ key, items }) => (
          <div key={key} className="space-y-2">
            {/* 섹션 라벨 */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-text-muted">
                {key}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {items.map((item) => (
              <NotiCard
                key={item.id}
                item={item}
                categoryName={categoryName(item.notiCategory)}
                onRead={handleMarkRead}
              />
            ))}
          </div>
        ))}
      </div>
    </Page>
  );
}
